import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import AnimeCard from "@/components/feature/AnimeCard";
import usePageMeta from "@/hooks/usePageMeta";
import { animeList, newsList, categoryMeta, fmtDate } from "@/mocks/anime";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim();
  const [keyword, setKeyword] = useState(q);
  const [tab, setTab] = useState<"all" | "anime" | "news">("all");

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  usePageMeta(
    q ? `妖精动漫 - 搜索“${q}”的结果` : "妖精动漫 - 站内搜索",
    q
      ? `在妖精动漫中搜索“${q}”，可同时查找相关动漫作品与资讯文章，支持按类型切换结果。`
      : "妖精动漫站内搜索，支持动漫作品与资讯文章联合搜索，输入关键词即可找到想看的内容。"
  );

  const kw = q.toLowerCase();
  const animeResults = kw
    ? animeList.filter((item) =>
        [item.title, item.originalTitle, item.type, item.region, item.tags.join(""), item.summary]
          .join(" ")
          .toLowerCase()
          .includes(kw)
      )
    : [];
  const newsResults = kw
    ? newsList.filter((item) => [item.title, item.summary, item.category, item.content.map((c) => c.body).join("")].join(" ").toLowerCase().includes(kw))
    : [];

  const total = animeResults.length + newsResults.length;
  const showAnime = tab === "all" || tab === "anime";
  const showNews = tab === "all" || tab === "news";

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = keyword.trim();
    if (next) setSearchParams({ q: next });
    else setSearchParams({});
  };

  const suggestions = ["星空", "武侠", "治愈", "奇幻", "美食", "机甲"];

  return (
    <SiteLayout>
      <section className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 py-9 md:py-14">
          <h1 className="text-2xl md:text-3xl font-black tracking-tight text-background-50">
            {q ? "搜索结果" : "站内搜索"}
          </h1>
          <p className="mt-2 text-[13px] text-primary-200">
            {q ? (
              <>当前关键词：<span className="text-accent-400 font-semibold">“{q}”</span></>
            ) : (
              "同时搜索动漫作品与资讯文章，输入关键词开始探索。"
            )}
          </p>

          <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2.5 max-w-[640px]">
            <div className="flex items-center h-11 flex-1 rounded-full bg-background-50 px-4 gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入动漫名、类型或资讯关键词"
                aria-label="搜索关键词"
                className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
              {keyword && (
                <button type="button" onClick={() => setKeyword("")} aria-label="清空关键词" className="text-foreground-400 hover:text-foreground-700 cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-11 px-7 rounded-full bg-accent-500 hover:bg-accent-600 text-primary-950 text-[13px] font-bold transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-primary-400">试试：</span>
            {suggestions.map((s) => (
              <Link
                key={s}
                to={`/search?q=${encodeURIComponent(s)}`}
                className="px-2.5 py-1 rounded-full bg-primary-900 text-primary-100 text-[11px] hover:bg-primary-800 transition-colors cursor-pointer"
              >
                {s}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10">
        {q && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <p className="text-[13px] text-foreground-600">
              共找到 <span className="font-bold text-foreground-950">{total}</span> 条结果
              <span className="ml-2 text-[12px] text-foreground-400">（动漫 {animeResults.length} · 资讯 {newsResults.length}）</span>
            </p>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit">
              {[
                { key: "all", label: "全部" },
                { key: "anime", label: `动漫 ${animeResults.length}` },
                { key: "news", label: `资讯 ${newsResults.length}` },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key as "all" | "anime" | "news")}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    tab === item.key ? "bg-primary-900 text-background-50" : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {!q ? (
          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <h2 className="text-[15px] font-bold text-foreground-950">从热门分类开始</h2>
            <p className="mt-1.5 text-[13px] text-foreground-500">或者直接搜索作品名、类型、剧情关键词。</p>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {categoryMeta.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2.5 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span className="w-8 h-8 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center shrink-0">
                    <i className={`${cat.icon} text-sm`} />
                  </span>
                  <span className="text-[13px] font-medium text-foreground-900">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        ) : total === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <h2 className="mt-4 text-[15px] font-semibold text-foreground-950">没有找到“{q}”相关的内容</h2>
            <p className="mt-2 text-[13px] text-foreground-500">换个更短的关键词试试，或者浏览下面的推荐内容。</p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              {["星空", "奇幻", "治愈"].map((s) => (
                <Link
                  key={s}
                  to={`/search?q=${encodeURIComponent(s)}`}
                  className="px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-[12px] text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  搜索“{s}”
                </Link>
              ))}
            </div>
            <div className="mt-8 text-left">
              <h3 className="text-[13px] font-semibold text-foreground-800 mb-3">热门推荐</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {[...animeList].sort((a, b) => b.views - a.views).slice(0, 4).map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {showAnime && animeResults.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-1 h-4 rounded-full bg-accent-500" />
                  <h2 className="text-[16px] font-bold text-foreground-950">动漫作品</h2>
                  <span className="text-[12px] text-foreground-400">{animeResults.length} 部</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {animeResults.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
              </div>
            )}

            {showAnime && animeResults.length === 0 && tab === "anime" && (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
                <p className="text-[14px] font-medium text-foreground-800">没有匹配的动漫作品</p>
                <p className="mt-1.5 text-[12px] text-foreground-500">试试切换到资讯结果，或更换关键词。</p>
              </div>
            )}

            {showNews && newsResults.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-1 h-4 rounded-full bg-secondary-500" />
                  <h2 className="text-[16px] font-bold text-foreground-950">资讯文章</h2>
                  <span className="text-[12px] text-foreground-400">{newsResults.length} 篇</span>
                </div>
                <div className="space-y-3">
                  {newsResults.map((news) => (
                    <Link
                      key={news.id}
                      to={`/news/detail/${news.id}`}
                      className="group flex flex-col sm:flex-row gap-3 p-3 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <div className="w-full sm:w-[160px] h-[150px] sm:h-[96px] rounded-md overflow-hidden shrink-0 bg-background-200">
                        <img
                          src={news.cover}
                          alt={`${news.title}资讯缩略图`}
                          title={`${news.title} - 搜索结果`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex flex-col justify-center">
                        <div className="flex items-center gap-2 text-[11px] text-foreground-500">
                          <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-900 font-semibold">{news.category}</span>
                          <span>{fmtDate(news.date)}</span>
                        </div>
                        <h3 className="mt-1.5 text-[14px] font-semibold text-foreground-950 leading-6 line-clamp-2 group-hover:text-primary-700 transition-colors">
                          {news.title}
                        </h3>
                        <p className="mt-1 text-[12px] text-foreground-500 line-clamp-1">{news.summary}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {showNews && newsResults.length === 0 && tab === "news" && (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
                <p className="text-[14px] font-medium text-foreground-800">没有匹配的资讯文章</p>
                <p className="mt-1.5 text-[12px] text-foreground-500">试试切换到动漫结果，或更换关键词。</p>
              </div>
            )}
          </div>
        )}
      </section>
    </SiteLayout>
  );
}